import { Box, Checkbox, Chip, IconButton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { Comment, Edit, GppMaybe, KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { Fragment, useState } from "react";
import { translate } from "../../../../../translate/translate";
import { formatDate } from "../../../../../utils/format-date";
import { NpiChecklistItem, NpiChecklistItemStatus } from "../../../../../models/npi/npi-checklist";
import type { ChecklistSection } from "../types/npi-checklist.types";
import { CHECKLIST_ITEM_STATUS_STYLES, getChecklistPhaseColor } from "../constants/dashboard.constants";
import { splitItemDescription } from "../utils/checklist.utils";

interface NpiChecklistTableProps {
  sections: ChecklistSection[];
  onValidate?: (item: NpiChecklistItem) => void;
  onComment?: (item: NpiChecklistItem) => void;
  onEdit?: (item: NpiChecklistItem) => void;
}

type Column = {
  label: string;
  align?: "left" | "center" | "right";
  width?: number | string;
};

const COLUMNS: Column[] = [
  { label: "checklist_item", width: 60 },
  { label: "checklist_requirement_description" },
  { label: "checklist_responsible", width: 140 },
  { label: "days", align: "center", width: 60 },
  { label: "checklist_final_deadline", width: 130 },
  { label: "checklist_completion_date", width: 150 },
  { label: "status", width: 170 },
  { label: "actions", align: "center", width: 140 },
];

const SHIELD_COLORS: Record<NpiChecklistItemStatus, string> = {
  [NpiChecklistItemStatus.COMPLETED]: "#CBD5E1",
  [NpiChecklistItemStatus.ON_GOING]: "#6B7280",
  [NpiChecklistItemStatus.AT_RISK]: "#DC2626",
  [NpiChecklistItemStatus.NOT_APPLICABLE]: "#CBD5E1",
};

const accentSx = (color: string) => ({ boxShadow: `inset 4px 0 0 ${color}` });

const headerCellSx = { backgroundColor: "#F8FAFC", borderBottom: "1px solid #E2E8F0" };
const headerTextSx = { fontSize: "12px", fontWeight: 600, color: "#6B7280", textTransform: "uppercase", whiteSpace: "nowrap" };
const sectionCellSx = { backgroundColor: "#F8FAFC", py: 1 };
const sectionTextSx = { fontSize: "13px", fontWeight: 700, color: "#111827", textTransform: "uppercase" };
const subsectionCellSx = { backgroundColor: "#DBEAFE", py: 0.75, pl: 7 };
const subsectionTextSx = { fontSize: "13px", fontWeight: 700, color: "#2563EB", textTransform: "uppercase" };
const codeBadgeSx = {
  flex: "0 0 auto",
  width: "auto",
  px: 1.25,
  py: 0.5,
  borderRadius: "6px",
  backgroundColor: "#F1F3F6",
  color: "#6B7280",
  fontSize: "13px",
};

const isLate = (item: NpiChecklistItem) => !!item.closed_on && !!item.due_date && item.closed_on > item.due_date;

const formatOptionalDate = (date: string | null) => (date ? formatDate(date) : "-");

const hasItems = (section: ChecklistSection) =>
  section.subsections.some((subsection) => subsection.items.length > 0);

const toggleInSet = <T,>(set: Set<T>, value: T) => {
  const next = new Set(set);
  next.has(value) ? next.delete(value) : next.add(value);
  return next;
};

interface ItemRowProps {
  item: NpiChecklistItem;
  accentColor: string;
  checked: boolean;
  onToggle: () => void;
  onValidate?: (item: NpiChecklistItem) => void;
  onComment?: (item: NpiChecklistItem) => void;
  onEdit?: (item: NpiChecklistItem) => void;
}

const ItemRow = ({ item, accentColor, checked, onToggle, onValidate, onComment, onEdit }: ItemRowProps) => {
  const isDone = item.status === NpiChecklistItemStatus.COMPLETED;
  const statusStyle = item.status ? CHECKLIST_ITEM_STATUS_STYLES[item.status] : null;
  const { title, detail } = splitItemDescription(item.description);

  return (
    <TableRow
      hover
      sx={{
        "& > td": { verticalAlign: "top", py: 1.5 },
        ...(item.status === NpiChecklistItemStatus.AT_RISK && { backgroundColor: "#FEF2F2" }),
      }}>
      <TableCell sx={accentSx(accentColor)}>
        <Checkbox size="small" checked={checked} onChange={onToggle} />
      </TableCell>

      <TableCell sx={{ maxWidth: 0 }}>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <Box component="span" sx={codeBadgeSx}>
            {item.item}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: "15px", fontWeight: 600, color: "#111827" }}>{title}</Typography>
            {detail && (
              <Typography noWrap title={detail} sx={{ fontSize: "13px", color: "#6B7280" }}>
                {detail}
              </Typography>
            )}
          </Box>
        </Stack>
      </TableCell>

      <TableCell>{item.owner || "-"}</TableCell>
      <TableCell align="center">{item.days ?? "-"}</TableCell>
      <TableCell>{formatOptionalDate(item.due_date)}</TableCell>
      <TableCell sx={{ color: isLate(item) ? "#DC2626" : undefined }}>{formatOptionalDate(item.closed_on)}</TableCell>

      <TableCell>
        {statusStyle && (
          <Chip
            size="small"
            label={translate.t(statusStyle.label).toUpperCase()}
            sx={{
              fontSize: "12px",
              fontWeight: 700,
              borderRadius: "8px",
              color: statusStyle.color,
              backgroundColor: statusStyle.background,
            }}
          />
        )}
      </TableCell>

      <TableCell align="center">
        <Stack direction="row" spacing={0.5} justifyContent="center">
          <IconButton size="small" aria-label={translate.t("checklist_validate")} onClick={() => onValidate?.(item)}>
            <GppMaybe sx={{ color: item.status ? SHIELD_COLORS[item.status] : "#CBD5E1" }} />
          </IconButton>
          <IconButton size="small" aria-label={translate.t("checklist_comment")} onClick={() => onComment?.(item)}>
            <Comment sx={{ color: "#374151" }} />
          </IconButton>
          <IconButton size="small" aria-label={translate.t("edit")} disabled={isDone} onClick={() => onEdit?.(item)}>
            <Edit sx={{ color: isDone ? "#CBD5E1" : "#4B5563" }} />
          </IconButton>
        </Stack>
      </TableCell>
    </TableRow>
  );
};

const NpiChecklistTableComponent = ({ sections, onValidate, onComment, onEdit }: NpiChecklistTableProps) => {
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set(sections.slice(1).map((section) => section.code)));
  const [selected, setSelected] = useState<Set<number>>(new Set());

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            {COLUMNS.map((column) => (
              <TableCell key={column.label} align={column.align} sx={{ ...headerCellSx, width: column.width }}>
                <Typography component="span" sx={headerTextSx}>
                  {translate.t(column.label)}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {!sections.some(hasItems) && (
            <TableRow>
              <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 4, color: "#64748B" }}>
                {translate.t("checklist_no_items_found")}
              </TableCell>
            </TableRow>
          )}

          {sections.map((section) => {
            if (!hasItems(section)) return null;

            const isOpen = !collapsed.has(section.code);
            const accentColor = getChecklistPhaseColor(section.phaseIndex);

            return (
              <Fragment key={section.code}>
                <TableRow hover onClick={() => setCollapsed((prev) => toggleInSet(prev, section.code))} sx={{ cursor: "pointer" }}>
                  <TableCell colSpan={COLUMNS.length} sx={{ ...sectionCellSx, ...accentSx(accentColor) }}>
                    <IconButton size="small" sx={{ mr: 1 }}>
                      {isOpen ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                    </IconButton>
                    <Typography component="span" sx={sectionTextSx}>
                      {section.code}. {section.name}
                    </Typography>
                  </TableCell>
                </TableRow>

                {isOpen &&
                  section.subsections.map((subsection) => subsection.items.length > 0 && (
                    <Fragment key={subsection.code}>
                      {subsection.name && (
                        <TableRow>
                          <TableCell colSpan={COLUMNS.length} sx={{ ...subsectionCellSx, ...accentSx(accentColor) }}>
                            <Typography component="span" sx={subsectionTextSx}>
                              {subsection.code} {subsection.name}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}

                      {subsection.items.map((item) => (
                        <ItemRow
                          key={item.id}
                          item={item}
                          accentColor={accentColor}
                          checked={selected.has(item.id)}
                          onToggle={() => setSelected((prev) => toggleInSet(prev, item.id))}
                          onValidate={onValidate}
                          onComment={onComment}
                          onEdit={onEdit}
                        />
                      ))}
                    </Fragment>
                  ))}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default NpiChecklistTableComponent;
